/**
 * DefaultBranding - the built-in installation brand, and the TypeScript shape of
 * `GET /api/v1/branding` (SPEC-PLATFORM-BRANDING CONTRACT-001).
 *
 * This module is DATA AND TYPES ONLY: it imports nothing, reads nothing and
 * renders nothing, so every consumer - the provider, the pre-paint cache that
 * arrives with STORY-PB-APP-BRAND, and the tests - shares one definition of
 * "the record" and one definition of "what applies when there is no record".
 *
 * THE ONE VENDOR LITERAL LIVES HERE (RULE-012). Every other user-facing string
 * takes the product name from the BrandingProvider and the built-in logo path
 * from this file; the gate `tools/check-vendor-name-literals.py`
 * (STORY-PB-HYGIENE) ratchets on a baseline that lists exactly this file. The
 * `brand-ok` marker below is authored now so that gate lands green.
 *
 * THE FOOTER HALF OF `DefaultBranding` IS DELIBERATELY EMPTY. `enabled: false`
 * with empty text means a failed or absent read renders NO footer CONTENT; the
 * fact that the read failed is carried by the provider's `state` field, never
 * by an invented value (RULE-001, D-BR-15). A default that carried a plausible
 * legal line would be the exact failure that rule exists to prevent.
 *
 * @module branding/defaults
 * @see SPEC-PLATFORM-BRANDING-CONTRACT-001
 * @see SPEC-PLATFORM-BRANDING-RULE-001 / RULE-012 / RULE-016
 * @see SPEC-PLATFORM-BRANDING-UI-004
 */

// ---------------------------------------------------------------------------
// The record shape (CONTRACT-001, field for field)
// ---------------------------------------------------------------------------

/**
 * A `{hu, en}` content pair. Hungarian and English are the ONLY content
 * languages of the branding record (RULE-002, the recorded divergence from
 * SPEC-FRONTEND-RULE-006's seven locales); the server always sends both halves,
 * so both are required here.
 *
 * Structurally assignable to `LocalisedValue` in
 * `web/src/components/shared/AppFooter`, whose halves are optional because it
 * also types a half-filled admin draft - a required field satisfies an optional
 * one, so the mount TASKs pass these values straight through.
 */
export interface LocalizedText {
  /** The Hungarian value. Also the fallback for every non-Hungarian locale. */
  hu: string;
  /** The English value. */
  en: string;
}

/** The three fixed legal links. Each is a plain URL string, never HTML. */
export interface LegalFooterLinks {
  /** Privacy notice / Adatkezelesi tajekoztato. */
  privacy: LocalizedText;
  /** Terms / ASZF. */
  terms: LocalizedText;
  /** Accessibility statement / Akadalymentesitesi nyilatkozat. */
  accessibility: LocalizedText;
}

/**
 * The `legal_footer` object of CONTRACT-001. `enabled: false` - the value on a
 * fresh install - means nothing renders at all (RULE-006b).
 */
export interface LegalFooterValues {
  /** Operator switch. `false` renders nothing at all. */
  enabled: boolean;
  /** The footer line. Operator-authored HTML, already sanitised server-side. */
  line: LocalizedText;
  /** The imprint block revealed by the disclosure. HTML in the same subset. */
  impressum: LocalizedText;
  /** The three fixed legal links. */
  links: LegalFooterLinks;
}

/** The resolved brand colours. `null` is "unset": the SPA keeps its palette. */
export interface BrandingColors {
  /** `#rrggbb`, or `null` when the operator set none. */
  primary: string | null;
  /** `#rrggbb`, or `null` when the operator set none. */
  secondary: string | null;
}

/**
 * The whole resolved record answered by `GET /api/v1/branding`. It mirrors the
 * gateway's `EffectiveBrandingRecord` (internal/repository/settings.go) field
 * for field, including the snake_case wire names - this type describes the
 * RESPONSE, so renaming a field here would silently stop matching it.
 */
export interface Branding {
  /** The installation product name. Never empty (RULE-016). */
  product_name: string;
  /** Absolute or same-origin logo path, or `null` for the built-in asset. */
  logo_url: string | null;
  /** Absolute or same-origin favicon path, or `null` for the built-in asset. */
  favicon_url: string | null;
  /** The resolved brand colours. */
  colors: BrandingColors;
  /** The login-page welcome text, by language. */
  login_welcome: LocalizedText;
  /** The legal footer. */
  legal_footer: LegalFooterValues;
}

// ---------------------------------------------------------------------------
// The built-in brand
// ---------------------------------------------------------------------------

/** An empty `{hu, en}` pair. A field that resolves empty is not rendered. */
const EMPTY_TEXT: LocalizedText = { hu: '', en: '' };

/**
 * The built-in installation brand and an EMPTY legal footer: what applies when
 * the branding read has not landed, or failed (RULE-001 - the brand fails open
 * and silently, the footer fails visible through the provider's `state`).
 *
 * Frozen so a consumer cannot mutate the fallback for every other consumer.
 */
// brand-ok: the built-in installation brand, the only sanctioned vendor literal
// per SPEC-PLATFORM-BRANDING RULE-012
export const DefaultBranding: Branding = Object.freeze({
  product_name: 'Verisa',
  logo_url: '/verisa_logo.png',
  favicon_url: '/verisa_logo.png',
  colors: Object.freeze({ primary: null, secondary: null }),
  login_welcome: EMPTY_TEXT,
  legal_footer: Object.freeze({
    enabled: false,
    line: EMPTY_TEXT,
    impressum: EMPTY_TEXT,
    links: Object.freeze({
      privacy: EMPTY_TEXT,
      terms: EMPTY_TEXT,
      accessibility: EMPTY_TEXT,
    }),
  }),
}) as Branding;
